import { api } from '../../../shared/api/client';
import {
  type ListEnvelope,
  normalizeListEnvelope,
  toGenexusParams,
} from '../../../shared/api/genexus-query';
import type { CrudApi } from '../../../shared/api/crud-hooks';
import type { DataTableQuery } from '../../../shared/components/DataTable';
import type {
  EmployeeNotaFiscalDto,
  EmployeeNotaFiscalInput,
  EmployeeNotaFiscalListItem,
  NotaFiscalEspelho,
  NotaFiscalStatus,
} from './types';

/** Filtros adicionais do List da NF Colaborador (param qualificado `statusNotaFiscal`). */
export interface NotasFiscaisListExtraParams {
  statusNotaFiscal?: NotaFiscalStatus;
  clienteCodigo?: number;
  mes?: number;
  ano?: number;
}

/**
 * CRUD do rascunho NSO aninhado em um Colaborador. `employeeId` fecha no
 * escopo. Update/Delete são bloqueados server-side quando `status != NSO`.
 * Transições de estado vão pelos endpoints custom em `workflowApi` abaixo.
 */
export function createEmployeeNotasFiscaisApi(
  employeeId: number,
  extra: NotasFiscaisListExtraParams = {},
): CrudApi<EmployeeNotaFiscalListItem, EmployeeNotaFiscalDto, EmployeeNotaFiscalInput, number> {
  const BASE = `/v1/employees/${employeeId}/notas-fiscais`;

  return {
    async list(query: DataTableQuery): Promise<ListEnvelope<EmployeeNotaFiscalListItem>> {
      const params = { ...toGenexusParams(query), ...extra };
      const { data } = await api.get<{
        data: EmployeeNotaFiscalListItem[];
        meta: { total: number; pagina?: number; porPagina?: number };
      }>(BASE, { params });
      return normalizeListEnvelope(data);
    },

    async get(id: number): Promise<EmployeeNotaFiscalDto> {
      const { data } = await api.get<{ data: EmployeeNotaFiscalDto }>(`${BASE}/${id}`);
      return data.data;
    },

    async create(input: EmployeeNotaFiscalInput): Promise<EmployeeNotaFiscalDto> {
      const { data } = await api.post<{ data: EmployeeNotaFiscalDto }>(BASE, input);
      return data.data;
    },

    async update(id: number, input: EmployeeNotaFiscalInput): Promise<EmployeeNotaFiscalDto> {
      const { data } = await api.put<{ data: EmployeeNotaFiscalDto }>(`${BASE}/${id}`, input);
      return data.data;
    },

    async remove(id: number): Promise<void> {
      await api.delete(`${BASE}/${id}`);
    },
  };
}

/** Espelho da competência, sem gravar. Mesma conta de `getNotaFiscalEspelho`. */
export async function fetchNotaFiscalEspelho(
  employeeId: number,
  input: EmployeeNotaFiscalInput,
): Promise<NotaFiscalEspelho> {
  const { data } = await api.get<{ data: NotaFiscalEspelho } | NotaFiscalEspelho>(
    `/v1/employees/${employeeId}/notas-fiscais/espelho`,
    { params: input },
  );
  if (data && typeof data === 'object' && 'data' in data && data.data) return data.data;
  return data as NotaFiscalEspelho;
}

/** Endpoints custom de workflow (POST sem body, exceto reprovar). */
export async function solicitarNotaFiscal(
  employeeId: number,
  id: number,
): Promise<EmployeeNotaFiscalDto> {
  const { data } = await api.post<{ data: EmployeeNotaFiscalDto }>(
    `/v1/employees/${employeeId}/notas-fiscais/${id}/solicitar`,
  );
  return data.data;
}

export async function aprovarNotaFiscal(
  employeeId: number,
  id: number,
): Promise<EmployeeNotaFiscalDto> {
  const { data } = await api.post<{ data: EmployeeNotaFiscalDto }>(
    `/v1/employees/${employeeId}/notas-fiscais/${id}/aprovar`,
  );
  return data.data;
}

export async function reprovarNotaFiscal(
  employeeId: number,
  id: number,
  motivoReprovacao: string,
): Promise<EmployeeNotaFiscalDto> {
  const { data } = await api.post<{ data: EmployeeNotaFiscalDto }>(
    `/v1/employees/${employeeId}/notas-fiscais/${id}/reprovar`,
    { motivoReprovacao },
  );
  return data.data;
}

/**
 * Upload do PDF via multipart/form-data (campo `file`). BFF valida magic-bytes
 * `%PDF-` e limite 10 MB antes de reencaixar em JSON+base64 pro upstream.
 * Transita `SOL → PAP` no servidor (dispara Trn `ColaboradorNotaFiscalAnexo`).
 */
export async function uploadNotaFiscalFile(
  employeeId: number,
  id: number,
  file: File,
): Promise<EmployeeNotaFiscalDto> {
  const fd = new FormData();
  fd.append('file', file, file.name);
  const { data } = await api.post<{ data: EmployeeNotaFiscalDto }>(
    `/v1/employees/${employeeId}/notas-fiscais/${id}/file`,
    fd,
    // axios injeta o `multipart/form-data; boundary=...` quando recebe FormData.
  );
  return data.data;
}

/**
 * Baixa o PDF como Blob. BFF unwrappa o SDT do upstream e proxia o blob com
 * `Content-Type: application/pdf`. UI usa `URL.createObjectURL` pra abrir em
 * nova aba (preview inline) ou disparar download.
 */
export async function downloadNotaFiscalFile(employeeId: number, id: number): Promise<Blob> {
  const res = await api.get<Blob>(`/v1/employees/${employeeId}/notas-fiscais/${id}/file`, {
    responseType: 'blob',
  });
  return res.data;
}
